import React, { useEffect, useState } from 'react';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import LoadingScreen from 'src/Loading/LoadingScreen';
import { fetchBankQuestionsApi } from 'src/services/question';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
ChartJS.register(ArcElement, Tooltip, Legend);


interface Factor {
  id: string;
  key: string;
  value: string;
  category: string;
}

interface ComplianceItem {
  id: string;
  name: string;
  version: string;
  domain?: { id: string; name: string };
  latest_comment: string | null;
}

interface Control {
  id: string;
  name: string;
  factors: Factor[];
  compliance?: ComplianceItem | null;
}

interface Question {
  id: string;
  question_en: string;
  controls: Control[];
}

const QuestionControlTree: React.FC = () => {
  const [data, setData] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const [expandedControls, setExpandedControls] = useState<Set<string>>(new Set());
  const [selectedQuarter, setSelectedQuarter] = useState<string>('Q1');
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetchBankQuestionsApi(selectedQuarter, selectedYear);
      setData(res.data);
    } catch (error) {
      console.error('Error fetching bank questions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedQuarter, selectedYear]);

  const toggleQuestion = (id: string) => {
    const newSet = new Set(expandedQuestions);
    newSet.has(id) ? newSet.delete(id) : newSet.add(id);
    setExpandedQuestions(newSet);
  };

  const toggleControl = (id: string) => {
    const newSet = new Set(expandedControls);
    newSet.has(id) ? newSet.delete(id) : newSet.add(id);
    setExpandedControls(newSet);
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className='root'>

    <div className="container-fluid mt-4">
      <h4 className="mb-3">Bank Questions & Controls</h4>

      <div className="d-flex gap-3 mb-4">
        <div>
          <label className="form-label">Quarter</label>
          <select className="form-select" value={selectedQuarter} onChange={(e) => setSelectedQuarter(e.target.value)}>
            {['Q1', 'Q2', 'Q3', 'Q4'].map((q) => (
              <option key={q} value={q}>{q}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="form-label">Year</label>
          <select className="form-select" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
            {[2023, 2024, 2025, 2026].map((y) => (
              <option key={y} value={y.toString()}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="accordion">
        {data.map((question) => (
          <div key={question.id} className="card mb-2 shadow-sm">
            <div
              className="card-header d-flex justify-content-between align-items-center cursor-pointer"
              onClick={() => toggleQuestion(question.id)}
              style={{ background: '#f8f9fa' }}
            >
              <div>
                <span className="text-primary fw-semibold me-1">Question:</span>
                {question.question_en}
                <button className="btn btn-warning btn-sm ms-3" disabled>Pending</button>
              </div>
              <FaChevronDown className={`transition-transform ${expandedQuestions.has(question.id) ? 'rotate-180' : ''}`} />
            </div>

            <AnimatePresence initial={false}>
              {expandedQuestions.has(question.id) && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="card-body bg-light border-top">
                    {question.controls.map((control) => (
                      <div key={control.id} className="mb-4">
                        <div
                          className="d-flex justify-content-between align-items-center cursor-pointer"
                          onClick={() => toggleControl(control.id)}
                        >
                          <strong>
                            <span className="text-success fw-semibold me-1">Control:</span>
                            {control.name}
                          </strong>
                          <FaChevronRight className={`transition-transform ${expandedControls.has(control.id) ? 'rotate-90' : ''}`} />
                        </div>

                        <AnimatePresence initial={false}>
                          {expandedControls.has(control.id) && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <div className="mt-3 ps-3 border-start border-2">
                                {/* Compliances */}
                               {control.compliance && (
                                  <div className="mb-4">
                                    <span className="badge bg-info text-dark mb-2">Compliance</span>
                                    <div className="list-group">
                                      <div className="list-group-item list-group-item-light mb-2">
                                        <div className="fw-semibold text-dark">
                                          {control.compliance.name}
                                          <span className="text-muted small"> (v{control.compliance.version})</span>
                                        </div>
                                        {control.compliance.domain?.name && (
                                          <div className="text-muted small mb-1">
                                            Domain: {control.compliance.domain.name}
                                          </div>
                                        )}
                                        <div className="text-muted small fst-italic">
                                          Latest Comment: {control.compliance.latest_comment || 'No comment'}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {/* Factors */}
                                <div className="row g-3">
                                  <div className="col-md-6">
                                    <div className="border rounded p-3 bg-white shadow-sm">
                                      <span className="badge bg-secondary mb-2">Factors</span>
                                      <ul className="list-unstyled mb-0">
                                        {control.factors.map((factor) => (
                                          <li key={factor.id} className="mb-2">
                                            <div className="fw-semibold">{factor.key}</div>
                                            <div className="text-muted small">
                                              Value: <strong>{factor.value || 'N/A'}</strong> | Category: {factor.category || 'N/A'}
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>

                                  <div className="col-md-6">
                                    <div className="card border-0 shadow-sm h-100">
                                      <div className="card-body d-flex justify-content-center align-items-center" style={{ height: 250 }}>
                                        <Doughnut
                                          data={{
                                            labels: control.factors.map((f) => f.key),
                                            datasets: [
                                              {
                                                data: control.factors.map((f) => Number(f.value) || 0),
                                                backgroundColor: ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b', '#858796'],
                                                borderWidth: 1,
                                              },
                                            ],
                                          }}
                                          options={{
                                            maintainAspectRatio: false,
                                            plugins: {
                                              legend: {
                                                position: 'bottom',
                                                labels: {
                                                  boxWidth: 12,
                                                  padding: 15,
                                                  font: { size: 12 },
                                                },
                                              },
                                            },
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
        </div>

  );
};

export default QuestionControlTree;
